import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Button } from '../atoms';
import { DataTable, StatCard, useToast, ManageParentMembersModal } from '../organisms';
import { EntityHeader } from '../shell';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * ParentClientRollup — /v2/parent-clients/:id
 *
 * Domain 17.2 parent-organization roll-up view. Reads the
 * GET /api/parent-client-rollup/:parentId contract (src/routes/crm.js):
 *   { parent,
 *     subsidiaries:[{ client, assessment_count, latest_score, latest_confidence,
 *       latest_assessed_at, latest_instrument, referral_count,
 *       enrollment_count, open_invoice_total? }],
 *     totals:{ subsidiary_count, assessed_count, assessment_count,
 *       referral_count, enrollment_count, avg_readiness, open_invoice_total? } }
 *
 * The measures are the supplier journey — how many member businesses have been
 * ASSESSED, where their readiness sits, how many referrals they've been routed
 * and how many programme enrollments they hold. (Was property/parcel counts and
 * a `ytd_savings` tax-reduction aggregate, none of which have an Ignite meaning
 * or a table behind them.)
 *
 * `open_invoice_total` is OMITTED by the backend for money-blind roles (the
 * office administrator) — the UI renders only the fields present, so the
 * money-blind view shows the journey measures alone. Each member business links
 * through to its own ClientDetail.
 */

interface ParentClient {
  id: string;
  name: string;
  short_name: string | null;
  notes: string | null;
}

interface SubsidiaryClient {
  id: string;
  name: string;
  short_name: string | null;
  status: string | null;
}

interface Subsidiary {
  client: SubsidiaryClient;
  assessment_count: number;
  /** Composite score of the most recent SCORED assessment; null = never scored. */
  latest_score: number | null;
  latest_confidence: number | null;
  latest_assessed_at: string | null;
  /** Instrument name + version that produced latest_score (e.g. "Business Readiness v1"). */
  latest_instrument: string | null;
  referral_count: number;
  enrollment_count: number;
  open_invoice_total?: number;
}

interface RollupTotals {
  subsidiary_count: number;
  /** Member businesses with at least one assessment on file. */
  assessed_count: number;
  assessment_count: number;
  referral_count: number;
  enrollment_count: number;
  /** Mean latest_score across the businesses that HAVE one; null when none do. */
  avg_readiness: number | null;
  open_invoice_total?: number;
}

interface Rollup {
  parent: ParentClient;
  subsidiaries: Subsidiary[];
  totals: RollupTotals;
}

const money = (v: number | null | undefined) =>
  v != null ? `$${Math.round(v).toLocaleString()}` : '—';

/** A readiness score. null means "not assessed yet", which is NOT zero. */
const score = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : Number(v).toFixed(1);

export function ParentClientRollup() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [membersOpen, setMembersOpen] = useState(false);
  // Add clients / Remove member both edit an existing parent's membership
  // (POST/DELETE /api/parent-clients/:id/members) → gate on parent_client.can_edit.
  const canEditParent = useEntityPermission('parent_client', 'can_edit');

  const rollupQuery = useQuery<Rollup>({
    queryKey: ['parent-client-rollup', id],
    queryFn: ({ signal }) => api.get<Rollup>(`/api/parent-client-rollup/${id}`, signal),
    enabled: !!id,
  });

  // The money aggregate is present only when the backend returned it (money-sighted
  // roles). When absent (money-blind administrator), the money column/tile hides.
  const showMoney = !!rollupQuery.data && 'open_invoice_total' in rollupQuery.data.totals;

  // RH§2 — remove a client from this parent (clears its parent_client_id).
  const removeMutation = useMutation<unknown, Error, string>({
    mutationFn: (clientId) => api.delete(`/api/parent-clients/${id}/members/${clientId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent-client-rollup', id] });
      queryClient.invalidateQueries({ queryKey: ['assignable-clients', id] });
      toast({ variant: 'success', message: 'Removed from this parent.' });
    },
    onError: (err) => toast({ variant: 'error', message: err instanceof ApiClientError ? err.message : 'Could not remove the client.' }),
  });

  const columns = useMemo<ColumnDef<Subsidiary>[]>(() => {
    const cols: ColumnDef<Subsidiary>[] = [
      { id: 'name', header: 'Business', cell: ({ row }) => row.original.client.name },
      { id: 'status', header: 'Status', cell: ({ row }) => (row.original.client.status || '—').replace(/_/g, ' ') },
      {
        id: 'readiness',
        header: 'Readiness',
        // The instrument + version that produced the score travels with it —
        // a score you cannot tie to its questions cannot be compared.
        cell: ({ row }) => {
          const s = row.original;
          if (s.latest_score === null) {
            return <span style={{ color: 'var(--color-ink-3)' }}>Not assessed</span>;
          }
          return (
            <span>
              {score(s.latest_score)}
              {s.latest_instrument && (
                <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-xs)' }}> · {s.latest_instrument}</span>
              )}
            </span>
          );
        },
      },
      { accessorKey: 'assessment_count', header: 'Assessments', cell: ({ getValue }) => (getValue() as number).toLocaleString() },
      { accessorKey: 'referral_count', header: 'Referrals', cell: ({ getValue }) => (getValue() as number).toLocaleString() },
      { accessorKey: 'enrollment_count', header: 'Enrollments', cell: ({ getValue }) => (getValue() as number).toLocaleString() },
    ];
    if (showMoney) {
      cols.push(
        { accessorKey: 'open_invoice_total', header: 'Open invoices', cell: ({ getValue }) => money(getValue() as number | undefined) },
      );
    }
    if (canEditParent) {
      cols.push({
        id: 'remove',
        header: '',
        cell: ({ row }) => (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); removeMutation.mutate(row.original.client.id); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-status-red)', font: 'inherit' }}
            title="Remove this business from the parent organization"
          >Remove</button>
        ),
      });
    }
    return cols;
  }, [showMoney, removeMutation, canEditParent]);

  if (rollupQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading roll-up…</p>;
  }

  if (rollupQuery.error) {
    const notFound = rollupQuery.error instanceof ApiClientError && rollupQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Parent organization not found' : 'Failed to load roll-up'}</h2>
        <p style={{ margin: 0 }}>{(rollupQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/parent-clients')}>
            Back to Parent organizations
          </Button>
        </p>
      </div>
    );
  }

  if (!rollupQuery.data) return null;

  const { parent, subsidiaries, totals } = rollupQuery.data;

  return (
    <>
      <EntityHeader
        prefix="Parent organization"
        title={parent.name}
        subtitle={parent.short_name && parent.short_name !== parent.name ? parent.short_name : undefined}
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate('/parent-clients')}>
              Back
            </Button>
            {/* RH§2 — add businesses to this parent (searchable multi-select). */}
            {canEditParent && (
              <Button variant="primary" size="sm" onClick={() => setMembersOpen(true)}>
                Add businesses
              </Button>
            )}
          </>
        }
        kpis={
          <>
            <StatCard label="Businesses" value={String(totals.subsidiary_count)} caption="under this parent organization" accent="hunter" />
            <StatCard
              label="Assessed"
              value={`${totals.assessed_count} of ${totals.subsidiary_count}`}
              caption="have an assessment on file"
              accent={totals.assessed_count < totals.subsidiary_count ? 'amber' : 'green'}
            />
            {/* "Not yet measured" is a different fact from "measured at zero" —
                the backend sends null for the former, and it renders as such. */}
            <StatCard
              label="Avg readiness"
              value={score(totals.avg_readiness)}
              caption={totals.avg_readiness === null ? 'no scores yet' : 'across assessed businesses'}
              accent="info"
            />
            <StatCard label="Referrals" value={totals.referral_count.toLocaleString()} caption="routed to partners" accent="info" />
            <StatCard label="Enrollments" value={totals.enrollment_count.toLocaleString()} caption="programme enrollments" accent="info" />
            {showMoney && (
              <StatCard label="Open invoices" value={money(totals.open_invoice_total)} caption="unpaid across the group" accent="amber" />
            )}
          </>
        }
      />

      {parent.notes && (
        <div style={{
          marginBottom: 'var(--sp-5)', padding: 'var(--sp-4)', background: 'var(--color-card)',
          border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', color: 'var(--color-ink-2)', fontSize: 'var(--fs-sm)',
        }}>{parent.notes}</div>
      )}

      <h2 style={{ margin: '0 0 var(--sp-4)', fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
        Member businesses
      </h2>

      {subsidiaries.length === 0 ? (
        <div style={{
          padding: 'var(--sp-6)', background: 'var(--color-card)', border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-lg)', color: 'var(--color-muted)', textAlign: 'center', fontSize: 'var(--fs-md)',
        }}>
          No businesses under this parent organization yet. Use “Add businesses” to pull them in.
        </div>
      ) : (
        <DataTable
          data={subsidiaries}
          columns={columns}
          onRowClick={(s) => navigate(`/clients/${s.client.id}`)}
        />
      )}

      {id && <ManageParentMembersModal parentId={id} parentName={parent.name} open={membersOpen} onClose={() => setMembersOpen(false)} />}
    </>
  );
}
